import { useReducer, useMemo, useCallback } from "react";
import { TiCardActor } from "../../../@types";
import {
  LoadingIndicator,
  NoData,
  RenderWhen
} from "../../../../../components";
import TiCardGeneralInfo from "./general/TiCardGeneralInfo";
import TiCardActors from "./general/TiCardActors";
import TiCardAnalyticsInfo from "./analytics/TiCardAnalyticsInfo";
import TiCardAdditionalInformation from "./additionalinformation/TiCardAdditionalInformation";
import TiCardPrevalenceGraph from "./analytics/TiCardPrevalenceGraph";
import TiCardAdditionalInformationReducer, {
  TiCardAdditionalInformationStateClosed
} from "./additionalinformation/TiCardAdditionalInformationReducer";
import { AdditionalInformationType } from "./additionalinformation/@ŧypes/TiCardAdditionalInformationReducer";
import useGetTiCardData from "../hooks/useGetTiCardData";
import useGetIOCSummary from "../../../../samples/hooks/useGetIOCSummary";
import { useLocalSampleSummaryParams } from "../../../../samples/hooks";
import SampleAttributionList from "../../../../samples/components/ioc/SampleAttributionList";
import Typography from "../../../../../components/typography/Typography";
import AIGeneratedIcon from "../../../../../components/icons/AIGeneratedIcon";
import { getCountryFromCountryCode } from "../../../../samples/components/ioc/utils";

interface Props {
  family?: string;
  noBorder?: boolean;
  prerenderGraphImage?: boolean;
}

const AttributionCardContent = ({
  family,
  noBorder,
  prerenderGraphImage
}: Props) => {
  const { data, isLoading } = useGetTiCardData(family);
  const { hash } = useLocalSampleSummaryParams();
  const { data: sampleIOCData, isLoading: iocDataLoading } = useGetIOCSummary(
    hash || ""
  );

  const combinedActors = useMemo(() => {
    const actorMap = new Map<string, TiCardActor>();
    const iocActors = sampleIOCData?.related_actors || [];
    const tiCardActors = data?.rl?.general?.actor || [];

    iocActors.forEach(actor =>
      actorMap.set(actor.name, {
        name: actor.name,
        country:
          getCountryFromCountryCode(actor.state_attribution?.country) ?? "",
        aliases: actor.aliases ?? []
      })
    );

    tiCardActors.forEach(actor => {
      if (actor.name) {
        actorMap.set(actor.name, actor);
      }
    });

    return Array.from(actorMap.values());
  }, [data, sampleIOCData]);

  const [state, dispatch] = useReducer(
    TiCardAdditionalInformationReducer,
    TiCardAdditionalInformationStateClosed
  );

  const handleActorClick = useCallback((actor: TiCardActor) => {
    dispatch({
      type: AdditionalInformationType.Actor,
      data: actor
    });
  }, []);

  const handleThreatNameDetailsClick = () => {
    if (!data?.rl.general) return;
    dispatch({
      type: AdditionalInformationType.Threat,
      data: data.rl.general
    });
  };

  const handleCloseDetails = () => {
    dispatch({
      type: AdditionalInformationType.Closed
    });
  };

  const containerClasses = [
    "ticard-content__container--attribution",
    noBorder ? "ticard-content__container--no-border" : ""
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={"ticard-content"}>
      <RenderWhen condition={isLoading && iocDataLoading}>
        <LoadingIndicator
          style={{ margin: "5rem 0" }}
          message={"Loading..."}
          data-testid="ticard_loading_indicator"
        />
      </RenderWhen>
      <RenderWhen
        condition={!isLoading && !iocDataLoading && !data && !sampleIOCData}
      >
        <span className={"ticard-no-content"} data-testid="ticard_no_data">
          No available Threat Intelligence data
        </span>
      </RenderWhen>
      {data && (
        <div className={containerClasses}>
          <RenderWhen
            condition={!iocDataLoading}
            fallback={<LoadingIndicator />}
          >
            <div className="ticard-content__sample_attribution">
              <div className="flex flex-gap-2 h3">
                <Typography variant="h3">Sample Attribution</Typography>
                <AIGeneratedIcon />
              </div>
              <SampleAttributionList sampleIOCData={sampleIOCData} hideActors />
            </div>
          </RenderWhen>
          <div className={"ticard-content__information"}>
            <div className="h3">
              <Typography variant="h3">Malware Family Insights</Typography>
            </div>
            <TiCardGeneralInfo
              data={data}
              onActorClick={handleActorClick}
              onOpenThreatNameDetails={handleThreatNameDetailsClick}
              hideActors
            />
          </div>

          <RenderWhen
            condition={!iocDataLoading}
            fallback={<LoadingIndicator />}
          >
            <div className="ticard-content__related-actors">
              <div className="ticard-content__related-actors-list">
                <span className="ticard-content__related-actors-label">
                  Related Threat Actors:{" "}
                </span>
                {combinedActors.length > 0 ? (
                  <TiCardActors
                    actors={combinedActors}
                    onActorClick={handleActorClick}
                  />
                ) : (
                  <NoData />
                )}
              </div>
            </div>
          </RenderWhen>

          <div className={"ticard-content__analytics_and_prevalence"}>
            <TiCardAnalyticsInfo analyticsData={data.rl.analytics} />
            <TiCardPrevalenceGraph
              prevelanceData={data.rl.analytics.prevalence || []}
              prerenderGraphImage={prerenderGraphImage}
            />
          </div>
          <TiCardAdditionalInformation
            state={state}
            closeDetails={handleCloseDetails}
          />
        </div>
      )}
    </div>
  );
};

export default AttributionCardContent;
